/**
 * Typography System
 *
 * Apple Liquid Glass-inspired typography with SF Pro scale.
 * Tighter headings, slightly larger body text for readability.
 */

// ============================================================================
// Font Families
// ============================================================================

export const fontFamilies = {
  // Primary font stack (for UI text).
  // The portals load Inter via `next/font/google` with `variable:
  // '--font-geist-sans'`, so we lead the stack with that CSS variable —
  // otherwise MUI sees only the literal "Inter" family name and falls
  // back to the system stack instead of the loaded Inter face.
  // Literal "Inter" stays in the stack as a fallback for non-Next.js
  // consumers (Storybook, test harnesses) where the CSS variable is unset.
  primary: [
    'var(--font-geist-sans)',
    '"Inter"',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(', '),

  // Monospace font stack (for code, IDs, amounts, tool tags).
  // Portals load JetBrains Mono via `next/font/google` with
  // `variable: '--font-geist-mono'`. Same rationale as `primary` —
  // CSS variable first so the loaded face actually renders.
  mono: [
    'var(--font-geist-mono)',
    '"JetBrains Mono"',
    'ui-monospace',
    'SFMono-Regular',
    '"SF Mono"',
    'Menlo',
    'Monaco',
    'Consolas',
    '"Liberation Mono"',
    '"Courier New"',
    'monospace',
  ].join(', '),

  // Display font (for headings - can be customized per tenant)
  display: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(', '),
} as const;

// ============================================================================
// Font Weights
// ============================================================================

export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// ============================================================================
// Font Sizes (rem based)
// ============================================================================

export const fontSizes = {
  '3xs': '0.625rem', // 10px
  '2xs': '0.6875rem', // 11px
  xs: '0.75rem', // 12px
  sm: '0.875rem', // 14px
  base: '1rem', // 16px
  lg: '1.125rem', // 18px
  xl: '1.25rem', // 20px
  '2xl': '1.5rem', // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem', // 48px
  '6xl': '3.75rem', // 60px
} as const;

// ============================================================================
// Icon Sizes (px values for MUI SvgIcon fontSize)
// ============================================================================

export const iconSizes = {
  '2xs': 12, // Inline indicators, status dots
  xs: 14, // Small inline icons (table cells, chips)
  sm: 16, // Compact icons (list items, chip icons)
  md: 18, // Default icons (buttons, menu items)
  base: 20, // Standard icons (nav items, action icons)
  lg: 24, // Large icons (section headers, empty states)
  xl: 32, // Hero icons (stat cards, feature highlights)
  '2xl': 40, // Display icons (analytics cards, big empty states)
  '3xl': 48, // Extra-large display icons
  '4xl': 64, // Upload icons, full-page empty states
} as const;

// ============================================================================
// Line Heights
// ============================================================================

export const lineHeights = {
  none: 1,
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2,
} as const;

// ============================================================================
// Letter Spacing
// ============================================================================

export const letterSpacings = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
} as const;

// ============================================================================
// MUI Typography Variants
// ============================================================================

export const typographyVariants = {
  fontFamily: fontFamilies.primary,
  fontSize: 15, // Apple base font size (15pt)

  h1: {
    fontFamily: fontFamilies.display,
    fontWeight: fontWeights.bold,
    fontSize: '2.125rem', // 34pt — Apple Large Title
    lineHeight: lineHeights.tight,
    letterSpacing: '-0.03em',
  },

  h2: {
    fontFamily: fontFamilies.display,
    fontWeight: fontWeights.bold,
    fontSize: '1.75rem', // 28pt — Apple Title 1
    lineHeight: lineHeights.tight,
    letterSpacing: '-0.02em',
  },

  h3: {
    fontFamily: fontFamilies.display,
    fontWeight: fontWeights.semibold,
    fontSize: '1.375rem', // 22pt — Apple Title 2
    lineHeight: lineHeights.snug,
    letterSpacing: '-0.02em',
  },

  h4: {
    fontFamily: fontFamilies.display,
    fontWeight: fontWeights.semibold,
    fontSize: '1.25rem', // 20pt — Apple Title 3
    lineHeight: lineHeights.snug,
    letterSpacing: '-0.01em',
  },

  h5: {
    fontFamily: fontFamilies.display,
    fontWeight: fontWeights.semibold,
    fontSize: '1.0625rem', // 17pt — Apple Headline
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  h6: {
    fontFamily: fontFamilies.display,
    fontWeight: fontWeights.semibold,
    fontSize: '0.9375rem', // 15pt — Apple Subheadline
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  subtitle1: {
    fontWeight: fontWeights.medium,
    fontSize: '1.0625rem', // 17pt
    lineHeight: lineHeights.relaxed,
    letterSpacing: letterSpacings.normal,
  },

  subtitle2: {
    fontWeight: fontWeights.medium,
    fontSize: '0.9375rem', // 15pt
    lineHeight: lineHeights.relaxed,
    letterSpacing: letterSpacings.normal,
  },

  body1: {
    fontWeight: fontWeights.regular,
    fontSize: '1.0625rem', // 17pt — Apple Body
    lineHeight: lineHeights.relaxed,
    letterSpacing: letterSpacings.normal,
  },

  body2: {
    fontWeight: fontWeights.regular,
    fontSize: '0.9375rem', // 15pt — Apple Callout
    lineHeight: lineHeights.relaxed,
    letterSpacing: letterSpacings.normal,
  },

  button: {
    fontWeight: fontWeights.semibold,
    fontSize: '0.9375rem', // 15pt — Apple button text
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
    textTransform: 'none' as const,
  },

  caption: {
    fontWeight: fontWeights.medium,
    fontSize: '0.8125rem', // 13pt — Apple Caption
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  overline: {
    // v3 DNA: mono uppercase micro-label. fontFamily switched to mono; size held
    // at 11px (Apple HIG Caption 2) to avoid ripple across the 100+ existing
    // overline consumers — 10px is a future tightening. letterSpacing tuned
    // down to 0.08em (v3 upper bound, still ≥ Apple HIG ≤13px uppercase floor).
    fontFamily: fontFamilies.mono,
    fontWeight: fontWeights.semibold,
    fontSize: '0.6875rem', // 11pt — Apple Caption 2
    lineHeight: lineHeights.normal,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
  },
} as const;

// ============================================================================
// Responsive Typography Sizes
// ============================================================================

export const responsiveTypography = {
  h1: {
    '@media (max-width: 599px)': {
      fontSize: '1.375rem', // 22pt on mobile
    },
    '@media (min-width: 600px) and (max-width: 900px)': {
      fontSize: '1.75rem', // 28pt on tablet
    },
  },
  h2: {
    '@media (max-width: 599px)': {
      fontSize: '1.25rem', // 20pt on mobile
    },
    '@media (min-width: 600px) and (max-width: 900px)': {
      fontSize: '1.375rem', // 22pt on tablet
    },
  },
  h3: {
    '@media (max-width: 599px)': {
      fontSize: '1.0625rem', // 17pt on mobile
    },
  },
} as const;

// ============================================================================
// Website / Marketing Typography Scale
// ============================================================================

/**
 * Creates a typography scale for marketing/website pages.
 * Larger than the app UI scale, designed for landing pages with proper hierarchy:
 * h1 (44px) > h2 (36px) > h3 (30px) > h4 (24px) > h5 (20px) > h6 (18px) > body1 (17px) > body2 (15px) > caption (13px)
 *
 * Headings can use a separate font family from body text (e.g., a brand display font).
 */
export function createWebsiteTypography(options?: { fontFamily?: string; headingFontFamily?: string }) {
  const body = options?.fontFamily ?? fontFamilies.primary;
  const heading = options?.headingFontFamily ?? body;

  return {
    fontFamily: body,
    h1: {
      fontFamily: heading,
      fontWeight: fontWeights.bold,
      fontSize: '2.75rem', // 44px
      lineHeight: lineHeights.tight,
      letterSpacing: '-0.03em',
      '@media (max-width: 600px)': { fontSize: '2rem' }, // 32px mobile
    },
    h2: {
      fontFamily: heading,
      fontWeight: fontWeights.bold,
      fontSize: '2.25rem', // 36px
      lineHeight: lineHeights.tight,
      letterSpacing: '-0.02em',
      '@media (max-width: 600px)': { fontSize: '1.75rem' }, // 28px mobile
    },
    h3: {
      fontFamily: heading,
      fontWeight: fontWeights.bold,
      fontSize: '1.875rem', // 30px
      lineHeight: lineHeights.snug,
      letterSpacing: '-0.015em',
      '@media (max-width: 600px)': { fontSize: '1.5rem' }, // 24px mobile
    },
    h4: {
      fontFamily: heading,
      fontWeight: fontWeights.bold,
      fontSize: '1.5rem', // 24px
      lineHeight: lineHeights.snug,
      letterSpacing: '-0.01em',
      '@media (max-width: 600px)': { fontSize: '1.25rem' }, // 20px mobile
    },
    h5: {
      fontFamily: heading,
      fontWeight: fontWeights.semibold,
      fontSize: '1.25rem', // 20px
      lineHeight: lineHeights.normal,
    },
    h6: {
      fontFamily: heading,
      fontWeight: fontWeights.semibold,
      fontSize: '1.125rem', // 18px
      lineHeight: lineHeights.normal,
    },
    subtitle1: {
      fontWeight: fontWeights.medium,
      fontSize: '1.0625rem', // 17px
      lineHeight: lineHeights.relaxed,
    },
    subtitle2: {
      fontWeight: fontWeights.medium,
      fontSize: '0.9375rem', // 15px
      lineHeight: lineHeights.relaxed,
    },
    body1: {
      fontSize: '1.0625rem', // 17px
      lineHeight: lineHeights.relaxed,
    },
    body2: {
      fontSize: '0.9375rem', // 15px
      lineHeight: lineHeights.relaxed,
    },
    button: {
      fontWeight: fontWeights.semibold,
      fontSize: '0.9375rem', // 15px
      textTransform: 'none' as const,
    },
    caption: {
      fontSize: '0.8125rem', // 13px
      lineHeight: lineHeights.normal,
    },
    overline: {
      fontWeight: fontWeights.semibold,
      fontSize: '0.75rem', // 12px
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const,
    },
  } as const;
}
